import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import {
  IconArrowLeft,
  IconCheck,
  IconTractor,
} from '@tabler/icons-react';

const URGENCY_LEVELS = [
  { value: 'low',      label: 'Low',      description: 'No immediate impact on operations'     },
  { value: 'medium',   label: 'Medium',   description: 'Affecting performance but still usable' },
  { value: 'high',     label: 'High',     description: 'Machine out of service'                 },
  { value: 'critical', label: 'Critical', description: 'Safety concern — do not use'            },
];

export default function JobNew() {
  const navigate = useNavigate();
  const { orgId, userProfile } = useAuth();
  const [machines, setMachines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [machineId, setMachineId] = useState('');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState('medium');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const snap = await getDocs(collection(db, 'organisations', orgId, 'machines'));
        setMachines(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
    };
    load();
  }, [orgId]);

  const selectedMachine = machines.find(m => m.id === machineId);

  const handleSave = async () => {
    if (!machineId) { setError('Please select a machine'); return; }
    if (!description.trim()) { setError('Please describe the issue'); return; }
    setSaving(true);
    setError('');
    try {
      await addDoc(collection(db, 'organisations', orgId, 'jobs'), {
        machineId,
        machineName: selectedMachine ? selectedMachine.makeLabel + ' ' + selectedMachine.modelLabel : null,
        machineReg: selectedMachine?.registration || null,
        description: description.trim(),
        urgency,
        notes: notes.trim() || null,
        status: 'pending',
        estimate: null,
        estimateApprovedAt: null,
        estimateDeclinedAt: null,
        completedAt: null,
        jobCardId: null,
        createdBy: userProfile?.id,
        createdByName: userProfile?.name,
        createdAt: serverTimestamp(),
      });
      toast.success('Job request submitted');
      navigate('/jobs');
    } catch (err) {
      console.error(err);
      setError('Failed to submit job request — please try again');
    }
    setSaving(false);
  };

  const urgencyColour = (u) => {
    if (u === 'critical') return 'var(--color-danger)';
    if (u === 'high') return 'var(--color-warning)';
    if (u === 'medium') return 'var(--color-brand)';
    return 'var(--color-text-tertiary)';
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 600, margin: '0 auto' }}>

        <div onClick={() => navigate('/jobs')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Job requests
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>New job request</h1>
        <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 24 }}>
          Submit a job request to your dealer for workshop work
        </p>

        {error && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 13, color: 'var(--color-danger)', marginBottom: 16 }}>
            {error}
          </div>
        )}

        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Select machine</div>
          {machines.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>No machines in your fleet — add a machine first</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
              {machines.map(machine => (
                <div
                  key={machine.id}
                  onClick={() => setMachineId(machine.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 'var(--radius-md)',
                    border: '0.5px solid',
                    borderColor: machineId === machine.id ? 'var(--color-brand)' : 'var(--color-border-light)',
                    background: machineId === machine.id ? 'var(--color-brand-light)' : 'transparent',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <IconTractor size={16} color="#185FA5" stroke={1.5} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: machineId === machine.id ? 'var(--color-brand)' : 'var(--color-text-primary)' }}>
                      {machine.makeLabel} {machine.modelLabel}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 1 }}>
                      {machine.registration || machine.serialNumber || '—'} · {machine.siteName}
                    </div>
                  </div>
                  {machineId === machine.id && <IconCheck size={15} color="var(--color-brand)" stroke={2} />}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Issue description</div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              Describe the issue <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <textarea
              placeholder="Describe the fault, symptoms, or work required..."
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={4}
              style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              Additional notes
            </label>
            <textarea
              placeholder="Any additional context for the workshop..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
        </Card>

        <Card style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Urgency</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {URGENCY_LEVELS.map(u => (
              <div
                key={u.value}
                onClick={() => setUrgency(u.value)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', borderRadius: 'var(--radius-md)',
                  border: '0.5px solid',
                  borderColor: urgency === u.value ? urgencyColour(u.value) : 'var(--color-border-light)',
                  background: urgency === u.value ? urgencyColour(u.value) + '12' : 'transparent',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, color: urgency === u.value ? urgencyColour(u.value) : 'var(--color-text-primary)', marginBottom: 2 }}>
                    {u.label}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{u.description}</div>
                </div>
                {urgency === u.value && <IconCheck size={15} color={urgencyColour(u.value)} stroke={2} />}
              </div>
            ))}
          </div>
        </Card>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => navigate('/jobs')}>Cancel</Button>
          <Button
            variant="primary"
            fullWidth
            onClick={handleSave}
            disabled={saving}
            loading={saving}
            icon={<IconCheck size={14} stroke={1.5} />}
          >
            {saving ? 'Submitting...' : 'Submit job request'}
          </Button>
        </div>

      </div>
    </Layout>
  );
}